/**
 * supplierDisposalApi.js
 * ──────────────────────
 * API module for all supplier-facing disposal Azure Function calls.
 * Mirrors the pattern of src/api/disposalApi.js.
 */

import { apiFetch } from '../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

// ---------------------------------------------------------------------------
// Supplier project + tool queries (via get_disposal_project_tools)
// ---------------------------------------------------------------------------

/**
 * Fetch projects visible to the caller's sites.
 * Uses mode=PROJECT_LIST — backend filters to caller's assigned sites.
 */
export async function getSupplierProjects(callerEmail) {
  const params = new URLSearchParams({ mode: 'PROJECT_LIST', caller_email: callerEmail });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `Fetch projects failed (${res.status})`);
  }
  return res.json();
}

/**
 * Fetch the tool pool for a specific project (supplier view).
 * Uses mode=PROJECT_TOOLS — returns all tools assigned to the project.
 */
export async function getSupplierToolPool(callerEmail, projectId) {
  const params = new URLSearchParams({
    mode: 'PROJECT_TOOLS',
    project_id: projectId,
    caller_email: callerEmail,
  });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `Fetch tool pool failed (${res.status})`);
  }
  return res.json();
}

/**
 * Fetch BOLs for a specific project.
 * Uses mode=BOL_LIST.
 */
export async function getProjectBols(callerEmail, projectId) {
  const params = new URLSearchParams({
    mode: 'BOL_LIST',
    project_id: projectId,
    caller_email: callerEmail,
  });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `Fetch BOLs failed (${res.status})`);
  }
  return res.json();
}

/**
 * Fetch tool details for specific tool IDs regardless of disposal_status.
 * Uses mode=BOL_TOOLS — returns tools even if SHIPPED or WRITTEN_OFF.
 */
export async function getBolToolDetails(callerEmail, projectId, toolIds) {
  const params = new URLSearchParams({
    mode: 'BOL_TOOLS',
    project_id: projectId,
    tool_ids: toolIds.join(','),
    caller_email: callerEmail,
  });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `Fetch BOL tools failed (${res.status})`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// BOL reopen (via manage_bol REOPEN)
// ---------------------------------------------------------------------------

/**
 * Reopen a closed BOL so tools can be removed.
 * Online-only — not queued offline.
 */
export async function reopenBol(callerEmail, bolId, projectId) {
  const res = await apiFetch(`${API_BASE_URL}/manage_bol`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'REOPEN',
      bol_id: bolId,
      project_id: projectId,
      reopened_by: callerEmail,
      caller_email: callerEmail,
    }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `Reopen failed (${res.status})`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Frost dismissal (via manage_frost_dismissal)
// ---------------------------------------------------------------------------

/**
 * Check if frost law warning has been dismissed for this project.
 * Returns { dismissed: boolean }
 */
export async function checkFrostDismissal(callerEmail, projectId) {
  const res = await apiFetch(`${API_BASE_URL}/manage_frost_dismissal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'CHECK',
      caller_email: callerEmail,
      project_id: projectId,
    }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `Frost check failed (${res.status})`);
  }
  return res.json();
}

/**
 * Dismiss the frost law warning for this project + site.
 */
export async function dismissFrost(callerEmail, projectId, siteId) {
  const res = await apiFetch(`${API_BASE_URL}/manage_frost_dismissal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'DISMISS',
      caller_email: callerEmail,
      project_id: projectId,
      site_id: siteId,
    }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `Frost dismiss failed (${res.status})`);
  }
  return res.json();
}
